import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageOwnContent, CLASS_MATERIALS_MANAGE_BASE } from '../../../config/contentRoutes';

interface Material {
  id: string;
  title: string;
  category: string;
  subject: string;
  grade: string;
  description: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  author: string;
}

const TeachingMaterials: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canManage = canManageOwnContent(currentUser?.role);

  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 샘플 데이터
  const sampleMaterials: Material[] = [
    {
      id: '4',
      title: 'AI 로봇 제작 프로젝트',
      category: 'project-guide',
      subject: 'technology',
      grade: 'middle',
      description: 'Micro:bit를 활용한 AI 로봇 제작 프로젝트 학습 자료입니다.',
      tags: ['AI', '로봇', 'Micro:bit', '프로젝트'],
      createdAt: '2024-01-15',
      updatedAt: '2024-01-15',
      author: '한지호'
    }
  ];

  useEffect(() => {
    setMaterials(sampleMaterials);
  }, []);

  const categories = [
    { id: 'lesson-plan', name: '수업 계획서', icon: '📋' },
    { id: 'project-guide', name: '프로젝트 가이드', icon: '🔧' },
    { id: 'assessment', name: '평가 자료', icon: '📊' },
    { id: 'worksheet', name: '학습 활동지', icon: '📝' },
    { id: 'presentation', name: '발표 자료', icon: '📽️' }
  ];

  const subjects = [
    { id: 'technology', name: '기술' },
    { id: 'computer', name: '정보' },
    { id: 'science', name: '과학' },
    { id: 'math', name: '수학' }
  ];

  const grades = [
    { id: 'elementary', name: '초등' },
    { id: 'middle', name: '중등' },
    { id: 'high', name: '고등' }
  ];

  const filteredMaterials = materials.filter(material => {
    const categoryMatch = selectedCategory === 'all' || material.category === selectedCategory;
    const subjectMatch = selectedSubject === 'all' || material.subject === selectedSubject;
    const searchMatch = material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       material.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       material.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return categoryMatch && subjectMatch && searchMatch;
  });

  const getCategoryName = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)?.name || categoryId;
  };

  const getSubjectName = (subjectId: string) => {
    return subjects.find(sub => sub.id === subjectId)?.name || subjectId;
  };

  const getGradeName = (gradeId: string) => {
    return grades.find(grade => grade.id === gradeId)?.name || gradeId;
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      backgroundColor: '#f5f5f5',
      overflow: 'auto'
    }}>
      {/* Header */}
      <div
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          padding: '1rem'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 0.5rem 0' }}>
              수업 자료
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
              평소 수업에서 사용하는 다양한 교과 자료들을 확인하세요
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => navigate(CLASS_MATERIALS_MANAGE_BASE)}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.6)',
                background: 'rgba(255,255,255,0.15)',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              내 자료 관리
            </button>
          )}
        </div>
      </div>

      <div style={{ width: '100%', padding: '1rem' }}>
        {/* 필터 및 검색 */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1rem', 
          marginBottom: '1rem', 
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <input
                type="text"
                placeholder="자료 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
              <span style={{ 
                position: 'absolute', 
                left: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#666'
              }}>
                🔍
              </span>
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                minWidth: '150px',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">전체 카테고리</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>

            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              style={{
                minWidth: '120px',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">전체 교과</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 자료 목록 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredMaterials.length === 0 ? (
            <div style={{ 
              backgroundColor: 'white', 
              padding: '1rem', 
              textAlign: 'center',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#666', marginBottom: '0.5rem' }}>
                검색 결과가 없습니다
              </h3>
              <p style={{ color: '#666', margin: 0 }}>
                다른 검색어나 필터를 시도해보세요
              </p>
            </div>
          ) : (
            filteredMaterials.map((material) => (
              <div 
                key={material.id} 
                style={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  if (material.id === '4') {
                    navigate('/class-materials/ai-robot-project');
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>
                        {material.title}
                      </h3>
                      <p style={{ color: '#666', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
                        {material.description}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem'
                      }}>
                        {getCategoryName(material.category)}
                      </span>
                      <span style={{
                        backgroundColor: 'transparent',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem'
                      }}>
                        {getSubjectName(material.subject)}
                      </span>
                      <span style={{
                        backgroundColor: 'transparent',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem'
                      }}>
                        {getGradeName(material.grade)}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                    {material.tags.map((tag, index) => (
                      <span 
                        key={index}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#666',
                          border: '1px solid #ddd',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '8px',
                          fontSize: '0.75rem'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: 0 }}>
                      작성자: {material.author} | 작성일: {material.createdAt}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TeachingMaterials;
